import { useEffect, useState, useMemo, useCallback } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isEditor } from "@/lib/roles";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Images, Plus, Trash2, Lock, FolderPlus, ChevronLeft, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";

function srcFor(p) {
  return p.image_url ? p.image_url : `${API}/photos/${p.id}/view`;
}

export default function Gallery() {
  const { user } = useAuth();
  const editor = isEditor(user?.role);
  const isAdmin = user?.role === "admin";
  const [photos, setPhotos] = useState([]);
  const [folders, setFolders] = useState([]);
  const [album, setAlbum] = useState("all");
  const [idx, setIdx] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ album: "", title: "", caption: "", event_date: "" });
  const [files, setFiles] = useState([]);
  const [folderOpen, setFolderOpen] = useState(false);
  const [newFolder, setNewFolder] = useState("");

  const load = () => {
    api.get("/photos").then((r) => setPhotos(r.data)).catch(() => {});
    api.get("/folders", { params: { scope: "photos" } }).then((r) => setFolders(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const albumNames = useMemo(() => {
    const set = new Set();
    folders.forEach((f) => set.add(f.name));
    photos.forEach((p) => p.album && set.add(p.album));
    return Array.from(set).sort();
  }, [folders, photos]);

  const filtered = useMemo(() => (album === "all" ? photos : photos.filter((p) => p.album === album)), [album, photos]);

  const upload = async () => {
    if (files.length === 0) return toast.error("Choose at least one image");
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("album", form.album || album !== "all" ? (form.album || album) : "General");
        fd.append("title", form.title || f.name);
        fd.append("caption", form.caption);
        fd.append("event_date", form.event_date);
        await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} photo(s) uploaded`);
      setOpen(false); setForm({ album: "", title: "", caption: "", event_date: "" }); setFiles([]); load();
    } catch { toast.error("Upload failed (editor access required)"); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/photos/${id}`); toast.success("Removed"); load(); };
  const createFolder = async () => {
    if (!newFolder.trim()) return toast.error("Folder name required");
    try { await api.post("/folders", { scope: "photos", name: newFolder.trim() }); toast.success("Folder created"); setFolderOpen(false); const n = newFolder.trim(); setNewFolder(""); await api.get("/folders", { params: { scope: "photos" } }).then((r) => setFolders(r.data)); setAlbum(n); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create folder"); }
  };

  // Lightbox navigation
  const openAt = (photo) => setIdx(filtered.findIndex((p) => p.id === photo.id));
  const close = () => setIdx(null);
  const prev = useCallback(() => setIdx((i) => (i == null ? i : (i - 1 + filtered.length) % filtered.length)), [filtered.length]);
  const next = useCallback(() => setIdx((i) => (i == null ? i : (i + 1) % filtered.length)), [filtered.length]);
  useEffect(() => {
    if (idx == null) return;
    const onKey = (e) => { if (e.key === "ArrowLeft") prev(); else if (e.key === "ArrowRight") next(); else if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, prev, next]);

  const current = idx != null ? filtered[idx] : null;
  const tabs = ["all", ...albumNames];

  return (
    <div data-testid="gallery-page">
      <PageHeader overline="Firm Hub" title="Photo Gallery" subtitle="Company milestones, team celebrations and community events — organized into folders.">
        {isAdmin && (
          <Dialog open={folderOpen} onOpenChange={setFolderOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="gallery-new-folder-btn"><FolderPlus className="h-4 w-4 mr-1.5" /> New Folder</Button></DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>New Photo Folder</DialogTitle><DialogDescription className="sr-only">Create an album folder.</DialogDescription></DialogHeader>
              <Input data-testid="new-folder-name" placeholder="e.g. Summer Social 2026" value={newFolder} onChange={(e) => setNewFolder(e.target.value)} />
              <DialogFooter><Button data-testid="create-folder-btn" onClick={createFolder}>Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {editor && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="gallery-upload-btn"><Plus className="h-4 w-4 mr-1.5" /> Upload Photos</Button></DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Upload Photos</DialogTitle><DialogDescription className="sr-only">Add photos to a folder.</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Folder / Album</Label>
                  <Input data-testid="photo-album" list="album-list" placeholder="e.g. Summer Social 2026" value={form.album} onChange={(e) => setForm({ ...form, album: e.target.value })} />
                  <datalist id="album-list">{albumNames.map((a) => <option key={a} value={a} />)}</datalist>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Title (optional)</Label><Input data-testid="photo-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                  <div><Label className="text-xs">Event date</Label><Input type="date" data-testid="photo-date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} /></div>
                </div>
                <div><Label className="text-xs">Caption</Label><Input data-testid="photo-caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} /></div>
                <div>
                  <Label className="text-xs">Images (select multiple)</Label>
                  <input data-testid="photo-file" type="file" accept="image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files))}
                    className="block w-full text-sm border border-border rounded-sm p-2 file:mr-3 file:border-0 file:bg-primary file:text-primary-foreground file:px-3 file:py-1 file:rounded-sm" />
                  {files.length > 0 && <p className="text-xs text-muted-foreground mt-1">{files.length} file(s) selected</p>}
                </div>
              </div>
              <DialogFooter><Button data-testid="photo-save-btn" onClick={upload} disabled={busy}>{busy ? "Uploading…" : "Upload"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="album-tabs">
        {tabs.map((a) => (
          <button key={a} data-testid="album-tab" onClick={() => setAlbum(a)}
            className={`px-3.5 py-1.5 text-sm rounded-sm border transition-colors ${album === a ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>
            {a === "all" ? "All Photos" : a}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Images} title="No photos here yet" hint={editor ? "Upload photos or create a folder using the buttons above." : "Photos will appear as they are added."} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-grid">
          {filtered.map((p) => (
            <div key={p.id} className="group relative aspect-square overflow-hidden rounded-sm border border-border bg-muted" data-testid="gallery-item">
              <img src={srcFor(p)} alt={p.title} draggable="false" onClick={() => openAt(p)}
                className="w-full h-full object-cover cursor-pointer transition-transform duration-300 group-hover:scale-105 select-none" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#051E36]/85 to-transparent p-2">
                <div className="text-white text-xs font-medium truncate">{p.title}</div>
                <div className="text-white/60 text-[10px] truncate">{p.album}</div>
              </div>
              {editor && (
                <button data-testid="gallery-delete-btn" onClick={() => remove(p.id)}
                  className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-[#051E36]/70 text-white p-1 rounded-sm hover:bg-destructive">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={idx != null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden" data-testid="gallery-lightbox">
          <DialogHeader className="sr-only"><DialogTitle>{current?.title}</DialogTitle><DialogDescription>Photo preview</DialogDescription></DialogHeader>
          {current && (
            <div className="dlp-protected">
              <div className="relative bg-[#051E36]">
                <img src={srcFor(current)} alt={current.title} draggable="false" className="w-full max-h-[70vh] object-contain select-none" />
                {filtered.length > 1 && (
                  <>
                    <button data-testid="lightbox-prev" onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-[#051E36]/60 hover:bg-[#051E36]/90 text-white rounded-full p-2"><ChevronLeft className="h-6 w-6" /></button>
                    <button data-testid="lightbox-next" onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#051E36]/60 hover:bg-[#051E36]/90 text-white rounded-full p-2"><ChevronRight className="h-6 w-6" /></button>
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-white/80 text-xs bg-[#051E36]/60 px-2 py-0.5 rounded-full">{idx + 1} / {filtered.length}</div>
                  </>
                )}
                <button onClick={close} className="absolute top-2 right-2 bg-[#051E36]/60 hover:bg-[#051E36]/90 text-white rounded-full p-1.5"><X className="h-4 w-4" /></button>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 bg-primary/10 text-primary rounded-sm">{current.album}</span>
                  {current.event_date && <span className="text-xs text-muted-foreground">{current.event_date}</span>}
                </div>
                <h3 className="font-display font-bold mt-1.5">{current.title}</h3>
                {current.caption && <p className="text-sm text-muted-foreground mt-1">{current.caption}</p>}
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-3"><Lock className="h-3 w-3" /> View only · downloading is disabled · use ← → to browse</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
